import React, { useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, Linking, TouchableOpacity, useWindowDimensions, Image } from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';

export default function DownloadPage() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  const openAppStore = () => {
    Linking.openURL('https://apps.apple.com/us/app/gematria-calculator-decode/id6744337544');
  };

  const openPlayStore = () => {
    Linking.openURL('https://play.google.com/store/apps/details?id=com.gematriacalculator.gematriacalculator');
  };

  // QR Code URLs generated from qr-server.com
  const iosQRCode = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://apps.apple.com/us/app/gematria-calculator-decode/id6744337544';
  const androidQRCode = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://play.google.com/store/apps/details?id=com.gematriacalculator.gematriacalculator';

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]}>
      <Text style={styles.title}>Download Gematria Calculator App</Text>

      <View style={styles.content}>
        <Text style={styles.intro}>
          Unlock the power of numbers—our Gematria Calculator now supports major Hebrew and English ciphers,
          with improved accuracy and custom cipher options! Available on both iOS and Android.
        </Text>

        <View style={[styles.downloadCards, isMobile && styles.verticalStack]}>
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Gematria Calculator for iOS</Text>
            <Text style={styles.cardText}>
              Available now on the Apple App Store. Calculate gematria values instantly on your iPhone or iPad.
            </Text>
            <TouchableOpacity style={styles.iosButton} onPress={openAppStore}>
              <Text style={styles.buttonText}>Download for iOS</Text>
            </TouchableOpacity>
            <View style={styles.qrContainer}>
              <Image
                source={{ uri: iosQRCode }}
                style={styles.qrCode}
              />
              <Text style={styles.qrCaption}>Scan to download</Text>
            </View>
          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>Gematria Calculator for Android</Text>
            <Text style={styles.badge}>Now Available</Text>
            <Text style={styles.cardText}>
              Download from Google Play Store. Works on Android 8.0 and later.
            </Text>
            <TouchableOpacity style={styles.androidButton} onPress={openPlayStore}>
              <Text style={styles.buttonText}>Download for Android</Text>
            </TouchableOpacity>
            <View style={styles.qrContainer}>
              <Image
                source={{ uri: androidQRCode }}
                style={styles.qrCode}
              />
              <Text style={styles.qrCaption}>Scan to download</Text>
            </View>
          </View>
        </View>

        <Text style={styles.heading}>App Features</Text>
        <View style={[styles.featureColumns, isMobile && styles.verticalStack]}>
          <View style={styles.featureColumn}>
            <Text style={styles.bulletPoint}>• Supports Hebrew and English Ciphers</Text>
            <Text style={styles.bulletPoint}>• Real-time calculation as you type</Text>
            <Text style={styles.bulletPoint}>• Letter-by-letter breakdown of values</Text>
            <Text style={styles.bulletPoint}>• Multi-cipher comparison in one view</Text>
          </View>
          <View style={styles.featureColumn}>
            <Text style={styles.bulletPoint}>• Educational insights about each cipher</Text>
            <Text style={styles.bulletPoint}>• Request custom ciphers through the app</Text>
            <Text style={styles.bulletPoint}>• Clean, simple interface</Text>
            <Text style={styles.bulletPoint}>• Works offline - no internet needed</Text>
          </View>
        </View>

        <Text style={styles.paragraph}>
          Explore hidden meanings in words with the Gematria Calculator. This app makes it easy to calculate
          the numerical value of any word or phrase using traditional gematria systems. Whether you're studying
          ancient texts, exploring spiritual symbolism, or just curious, this tool helps you decode with accuracy
          and ease.
        </Text>

        <Text style={styles.heading}>System Requirements</Text>
        <View style={[styles.requirementsGrid, isMobile && styles.verticalStack]}>
          <View style={styles.requirement}>
            <Text style={styles.requirementTitle}>iOS</Text>
            <Text style={styles.requirementText}>Requires iOS 14.0 or later</Text>
            <Text style={styles.requirementText}>Compatible with iPhone, iPad, and iPod touch</Text>
          </View>
          <View style={styles.requirement}>
            <Text style={styles.requirementTitle}>Android</Text>
            <Text style={styles.requirementText}>Requires Android 8.0 or later</Text>
            <Text style={styles.requirementText}>Available now on Google Play Store</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.surface,
    padding: 40,
  },
  containerMobile: {
    padding: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 30,
    textAlign: 'center',
  },
  content: {
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    color: colors.text,
    fontWeight: '500',
    marginBottom: 30,
    padding: 15,
    backgroundColor: colors.primarySurface,
    borderRadius: 8,
    textAlign: 'center',
  },
  downloadCards: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 40,
  },
  verticalStack: {
    flexDirection: 'column',
    gap: 15,
  },
  card: {
    flex: 1,
    backgroundColor: colors.surfaceElevated,
    borderRadius: 12,
    padding: 25,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 15,
    elevation: 5,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 10,
  },
  badge: {
    backgroundColor: colors.success,
    color: colors.onPrimary,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    fontSize: 12,
    fontWeight: '500',
    alignSelf: 'flex-start',
    marginBottom: 10,
  },
  cardText: {
    fontSize: 14,
    lineHeight: 22,
    color: colors.textSecondary,
    marginBottom: 20,
  },
  iosButton: {
    backgroundColor: '#007AFF',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  androidButton: {
    backgroundColor: '#34A853',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 15,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  qrContainer: {
    alignItems: 'center',
    marginTop: 15,
    paddingTop: 15,
    borderTopWidth: 1,
    borderTopColor: colors.divider,
  },
  qrCode: {
    width: 150,
    height: 150,
    borderRadius: 8,
    marginBottom: 8,
  },
  qrCaption: {
    fontSize: 12,
    color: colors.textSecondary,
    fontStyle: 'italic',
  },
  heading: {
    fontSize: 22,
    fontWeight: 'bold',
    color: colors.text,
    marginTop: 30,
    marginBottom: 20,
  },
  featureColumns: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 30,
  },
  featureColumn: {
    flex: 1,
    backgroundColor: colors.mutedSurface,
    padding: 15,
    borderRadius: 8,
  },
  bulletPoint: {
    fontSize: 14,
    lineHeight: 24,
    color: colors.text,
    marginBottom: 8,
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 25,
    color: colors.text,
    marginBottom: 15,
  },
  requirementsGrid: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 20,
    marginBottom: 40,
  },
  requirement: {
    flex: 1,
    backgroundColor: colors.surfaceElevated,
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  requirementTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 10,
  },
  requirementText: {
    fontSize: 13,
    lineHeight: 20,
    color: colors.textSecondary,
    marginBottom: 5,
  },
});
